import { ExternalLink, FileText } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import type { DiscriminatedPoliceReportUpdatedEvent } from '@/services/api/generated/taskActivityService';

import {
  EventActions,
  EventBody,
  EventContainer,
  EventHeader,
} from './EventPrimitives';

interface PoliceReportUpdatedEventProps {
  event: DiscriminatedPoliceReportUpdatedEvent;
  matterId: string;
}

export const PoliceReportUpdatedEvent: React.FC<
  PoliceReportUpdatedEventProps
> = ({ event, matterId }) => {
  const { payload, occurred_at, user, agent } = event;
  const { police_report, diff } = payload;

  // Get department name
  const departmentName =
    police_report.police_department?.name || 'Unknown Department';

  // Get report identifier
  const reportIdentifier = police_report.report_identifier;

  // Get attestation status
  const attestationStatus = police_report.attestation_status
    ? formatAttestationStatus(police_report.attestation_status)
    : null;

  // Check what was updated
  const hasReportIdentifierChange = 'report_identifier' in diff;
  const hasLocationChange = 'reported_location' in diff;
  const hasDescriptionChange = 'attestation_description' in diff;

  return (
    <EventContainer>
      <EventHeader
        icon={<FileText size={20} />}
        title="Police Report Updated"
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* Department Name */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-medium">{departmentName}</span>
            {attestationStatus && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="outline">{attestationStatus}</Badge>
              </>
            )}
          </div>

          {/* Report Identifier */}
          {reportIdentifier && (
            <div className="text-xs">
              <span className="text-slate-600">Report #:</span>{' '}
              <span className="font-mono text-[11px]">{reportIdentifier}</span>
              {hasReportIdentifierChange && (
                <span className="text-green-700 ml-1">(updated)</span>
              )}
            </div>
          )}

          {/* Reported Location */}
          {police_report.reported_location && (
            <div className="text-xs text-slate-600">
              {police_report.reported_location}
              {hasLocationChange && (
                <span className="text-green-700 ml-1">(updated)</span>
              )}
            </div>
          )}

          {/* Attestation Description */}
          {police_report.attestation_description && (
            <div className="text-xs text-slate-600 italic">
              {police_report.attestation_description}
              {hasDescriptionChange && (
                <span className="text-green-700 ml-1">(updated)</span>
              )}
            </div>
          )}
        </div>
      </EventBody>

      <EventActions>
        <a
          href={`/matters/${matterId}/police-reports/${police_report.id}`}
          className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
        >
          View Report
          <ExternalLink size={11} />
        </a>
      </EventActions>
    </EventContainer>
  );
};

// Helper function
function formatAttestationStatus(status: string): string {
  const statuses: Record<string, string> = {
    rejected_verified: 'Rejected (Verified)',
    unclear: 'Unclear',
    inferred: 'Inferred',
    attested_unverified: 'Attested (Unverified)',
    attested_verified: 'Attested (Verified)',
    verified_received: 'Received',
  };
  return statuses[status] || status;
}
